
import { useState, useEffect } from "react";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { TopMenu } from "./TopMenu";

const TimePreference2 = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [preferences, setPreferences] = useState({
    sunday: { active: false, time1: "12:30", time2: "12:30" },
    monday: { active: false, time1: "12:30", time2: "12:30" },
    tuesday: { active: false, time1: "12:30", time2: "12:30" },
    wednesday: { active: false, time1: "12:30", time2: "12:30" },
    thursday: { active: false, time1: "12:30", time2: "12:30" },
    friday: { active: false, time1: "12:30", time2: "12:30" },
    saturday: { active: false, time1: "12:30", time2: "12:30" },
  });

  // Fetch current user's preferences when component mounts
  useEffect(() => {
    const fetchUserPreferences = async () => {
      try {
        // Get current user session
        const { data: sessionData } = await supabase.auth.getSession();
        
        if (!sessionData.session) {
          console.log('No active session, redirecting to login');
          toast({
            title: "Authentication Required",
            description: "Please login to access time preferences.",
            variant: "destructive",
          });
          navigate('/login');
          return;
        }
        
        // Get the user's subscriber record
        const { data: subscriberData, error: subscriberError } = await supabase
          .from('subscribers')
          .select('subscriber_id, preferred_time_slots, onboarding_stage')
          .eq('email', sessionData.session.user.email)
          .single();
        
        if (subscriberError) {
          console.error('Error fetching subscriber data:', subscriberError);
          toast({
            title: "Error",
            description: "Could not load your preferences. Please try again later.",
            variant: "destructive",
          });
          return;
        }
        
        if (subscriberData) {
          console.log('Fetched subscriber data:', subscriberData);
          localStorage.setItem('subscriber_id', subscriberData.subscriber_id);
          
          // Convert from DB format to component state format
          if (subscriberData.preferred_time_slots) {
            const newPreferences = { ...preferences };
            
            Object.entries(subscriberData.preferred_time_slots).forEach(([day, dayPrefs]: [string, any]) => {
              if (newPreferences[day as keyof typeof newPreferences]) {
                newPreferences[day as keyof typeof newPreferences] = {
                  active: dayPrefs.active || false,
                  time1: dayPrefs.call_time_1 || "12:30",
                  time2: dayPrefs.call_time_2 || "12:30"
                };
              }
            });
            
            setPreferences(newPreferences);
          }
        } else {
          console.log('No subscriber data found, redirecting to registration');
          toast({
            title: "Registration Required",
            description: "Please complete registration first.",
            variant: "destructive",
          });
          navigate('/register');
        }
      } catch (error) {
        console.error('Error in fetchUserPreferences:', error);
        toast({
          title: "Error",
          description: "Failed to load your preferences. Please try again.",
          variant: "destructive",
        });
      }
    };
    
    fetchUserPreferences();
  }, [navigate, toast]);

  useEffect(() => {
    localStorage.setItem('userPreferences', JSON.stringify(preferences));
  }, [preferences]);

  const handleSavePreferences = async () => {
    const isAnyDayActive = Object.values(preferences).some(pref => pref.active);
    
    if (!isAnyDayActive) {
      toast({
        title: "Validation Error",
        description: "Please enable at least 1 day for scheduling",
        variant: "destructive",
      });
      return; // Prevent further execution
    }

    try {
      setIsLoading(true);
      const subscriber_id = localStorage.getItem('subscriber_id');
      
      if (!subscriber_id) {
        throw new Error('No subscriber ID found');
      }

      // Get current onboarding stage to determine if we should update it
      const { data: currentSubscriber, error: fetchError } = await supabase
        .from('subscribers')
        .select('onboarding_stage')
        .eq('subscriber_id', subscriber_id)
        .single();
        
      if (fetchError) {
        throw fetchError;
      }

      // Format preferences in the structure required by the database
      const formattedPreferences = Object.entries(preferences).reduce((acc, [day, pref]) => ({
        ...acc,
        [day]: {
          active: pref.active,
          call_time_1: pref.active ? pref.time1 : null,
          call_time_2: pref.active ? pref.time2 : null
        }
      }), {});

      // Determine if we should update the onboarding stage
      const updateData: any = {
        subscriber_id,
        preferred_time_slots: formattedPreferences
      };
      
      // If current stage is payment_complete, update to active
      if (currentSubscriber.onboarding_stage === 'payment_complete') {
        updateData.onboarding_stage = 'active';
      }

      // Update subscriber record with new preferences
      const { data, error } = await supabase.functions.invoke('update-subscriber', {
        body: { subscriberData: updateData }
      });

      if (error) throw error;
      
      toast({
        title: "Preferences Saved",
        description: "Your time preferences have been successfully saved.",
      });
      
      navigate('/test-call');
      
    } catch (error) {
      console.error('Error in save preferences flow:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to save preferences. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleTimeChange = (day: string, timeSlot: 'time1' | 'time2', time: string) => {
    setPreferences(prev => ({
      ...prev,
      [day]: { ...prev[day as keyof typeof prev], [timeSlot]: time }
    }));
  };

  const handleToggleDay = (day: string) => {
    setPreferences(prev => ({
      ...prev,
      [day]: { ...prev[day as keyof typeof prev], active: !prev[day as keyof typeof prev].active }
    }));
  };

  return (
    <div>
      <TopMenu/>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto bg-white p-8 rounded-lg shadow-sm">
          <h1 className="text-2xl font-semibold mb-8">Select Your Preferred Time Slots</h1>
          
          <div className="space-y-6">
            {/* Column Headers */}
            <div className="flex items-center justify-between">
              <div className="flex-1">
                {/* Day label area */}
              </div>
              <div className="flex items-center space-x-4">
                <span className="text-sm font-medium text-gray-600 w-32 text-center">Preferred Time 1</span>
                <span className="text-sm font-medium text-gray-600 w-32 text-center">Preferred Time 2</span>
              </div>
            </div>
            
            {/* Day rows */}
            {Object.entries(preferences).map(([day, { active, time1, time2 }]) => (
              <div key={day} className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <Switch
                    checked={active}
                    onCheckedChange={() => handleToggleDay(day)}
                    className="data-[state=checked]:bg-black data-[state=checked]:hover:bg-gray-800"
                  />
                  <span className="text-lg capitalize">{day}</span>
                </div>
                
                <div className="flex items-center space-x-4">
                  <input
                    type="time"
                    className={`px-3 py-2 rounded-lg border border-gray-200 text-sm w-32 ${
                      !active && 'opacity-50'
                    }`}
                    value={time1}
                    onChange={(e) => handleTimeChange(day, 'time1', e.target.value)}
                    disabled={!active}
                  />
                  <input
                    type="time"
                    className={`px-3 py-2 rounded-lg border border-gray-200 text-sm w-32 ${
                      !active && 'opacity-50'
                    }`}
                    value={time2}
                    onChange={(e) => handleTimeChange(day, 'time2', e.target.value)}
                    disabled={!active}
                  />
                </div>
              </div>
            ))}
          </div>

          <Button 
            className="w-full mt-8 bg-black hover:bg-gray-800 rounded-lg py-6 text-base font-medium"
            onClick={handleSavePreferences}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Preference'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TimePreference2;
